import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import * as _ from 'ramda';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import Helmet from 'react-helmet';
import { Row, Col, Spin } from 'antd';
import elasticlunr from 'elasticlunr';

import FilterInput from '../FilterInput';
import SheetTitle from '../SheetTitle';
import LoadingContainer from '../LoadingContainer';
import analytics from '../../helpers/analytics';
import { smoothScroll } from '../../helpers/view';

import c from './GroupedList.less';

class WrappedWithGrouped extends React.Component {
  lunrIndex = null;

  static propTypes = {
    app: PropTypes.object.isRequired,
    data: PropTypes.object.isRequired,
    loadData: PropTypes.func.isRequired,
    componentName: PropTypes.string,
    header: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    pathPrefix: PropTypes.string,
    itemRender: PropTypes.func.isRequired,
  };

  static defaultProps = {
    componentName: '',
    header: '',
    title: '',
    description: '',
    pathPrefix: null,
  };

  constructor(props) {
    super(props);

    elasticlunr.clearStopWords();
  }

  state = {
    showSearchResults: false,
    searchResults: [],
    grouped: {},
    term: null,
  };

  UNSAFE_componentWillMount() {
    if (!_.isEmpty(this.props.data)) {
      this._onDataLoad(this.props.data);
    }
  }

  componentDidMount() {
    this._browserRenderedAction();

    smoothScroll();

    analytics.pageview(`/catalog/${this.props.pathPrefix}`);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (!_.equals(nextProps.data, this.props.data)) {
      this._onDataLoad(nextProps.data);
    }
  }

  groupData = (data = {}) => {
    const alpha = /[A-z]/;
    const numeric = /[0-9]/;

    const unordered = _.compose(
      _.head,
      _.mapAccum((acc, series) => {
        const firstCharacter = series.name.charAt(0).toUpperCase();

        if (numeric.test(firstCharacter)) {
          if (!acc['#']) acc['#'] = [];

          acc['#'].push(series.id);
        }

        if (alpha.test(firstCharacter)) {
          if (!acc[firstCharacter]) acc[firstCharacter] = [];

          acc[firstCharacter].push(series.id);
        }

        return [acc, series];
      }, {}),
      _.map((id) => data[id]),
      _.keys,
    )(data);

    return _.compose(
      _.head,
      _.mapAccum((acc, key) => {
        acc[key] = _.compose(
          _.map((series) => series.id),
          _.sortBy(_.prop('name')),
          _.map((id) => data[id]),
        )(unordered[key]);

        return [acc, key];
      }, {}),
      _.sort((a, b) => String(a).localeCompare(b)),
      _.keys,
    )(unordered);
  };

  handleSearch = (term) => {
    const showSearchResults = term.length > 0;

    this.setState({
      showSearchResults,
      term,
      ...(showSearchResults ? {
        searchResults: this.lunrIndex.search(term, {
          bool: 'AND',
          expand: true,
        }),
      } : {}),
    });
  };

  _onDataLoad(series) {
    this.buildSearchIndex(series);
    this.setState({
      grouped: this.groupData(series.data),
    });
  }

  buildSearchIndex(series) {
    this.lunrIndex = elasticlunr(function build() {
      this.addField('name');
      this.setRef('id');

      Object.keys(series.data).forEach((id) => {
        this.addDoc(series.data[id]);
      });
    });
  }

  _browserRenderedAction() {
    if (this.props.app.renderedComponent === this.props.componentName) return;

    this.props.loadData();
  }

  render() {
    const { searchResults, showSearchResults, grouped } = this.state;
    const {
      data, header, title, description, itemRender,
    } = this.props;

    if (data.loading && Object.keys(data.data).length === 0) {
      return <LoadingContainer />;
    }

    return (
      <div className={c.container}>
        <Helmet defer={false}>
          <title>{title}</title>
          <meta name="description" content={description} />
          <meta property="og:title" content={title} />
          <meta property="og:description" content={description} />
        </Helmet>
        <SheetTitle title={header} rightBlock={<FilterInput onSearch={this.handleSearch} value={this.state.term} />} />
        <Spin size="large" spinning={data.loading}>
          {
            showSearchResults ? (
              <Row gutter={{ sm: 16, md: 20 }} className={c.listContainer}>
                {
                  searchResults
                    .map(({ ref }) => data.data[parseInt(ref, 10)])
                    .map((item) => (
                      <Col xs={24} sm={12} md={8} lg={6} key={item.id}>
                        {itemRender(item)}
                      </Col>
                    ))
                }
              </Row>
            ) : (
              <Fragment>
                {
                  Object.keys(grouped)
                    .map((letter) => (
                      <div key={letter}>
                        <h4 className={c.letter}>{letter.toUpperCase()}</h4>
                        <Row gutter={{ sm: 16, md: 20 }} className={c.listContainer}>
                          {
                            grouped[letter]
                              .map((id) => (
                                <Col xs={24} sm={12} md={8} lg={6} key={id}>
                                  {itemRender(data.data[id])}
                                </Col>
                              ))
                          }
                        </Row>
                      </div>
                    ))
                }
              </Fragment>
            )
          }
        </Spin>
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch, props) => bindActionCreators({
  loadData: props.loadData,
  dispatch,
}, dispatch);

const mapStateToProps = (store, props) => ({
  app: store.app,
  data: props.mapData(store),
});

export default connect(mapStateToProps, mapDispatchToProps)(WrappedWithGrouped);
